import React, { useState, useEffect, useCallback } from 'react'
import { Header } from '../components/layout/Header'

interface ForecastPoint {
  period: string
  forecast: number
  lower_bound: number
  upper_bound: number
  confidence: number
}

interface Forecast {
  entity_type: string
  entity_id: string
  metric: string
  model: string
  forecasts: ForecastPoint[]
  summary: {
    total_forecast: number
    average_forecast: number
    projected_growth_pct: number
    trend_direction: string
    trend_strength: number
  }
}

interface TrendData {
  current_value: number
  previous_value: number | null
  trend_direction: string
  yoy_growth_pct: number
  mom_growth_pct: number
  volatility: number
}

interface HistoricalPoint {
  month: string
  revenue: number
  orders: number
  units: number
  aov: number
  conversion_rate: number
  traffic: number
}

interface Scenario {
  name: string
  description: string
  adjustment_factor: number
  total_forecast: number
  difference_from_baseline: number
}

interface Anomaly {
  period: string
  value: number
  type: string
  severity: string
  deviation_percent: number
}

const API_BASE = '/api/forecasting'

const METRICS = [
  { id: 'revenue', name: 'Revenue', format: 'currency' },
  { id: 'orders', name: 'Orders', format: 'number' },
  { id: 'units', name: 'Units Sold', format: 'number' },
  { id: 'aov', name: 'Avg Order Value', format: 'currency' },
  { id: 'conversion_rate', name: 'Conversion Rate', format: 'percent' },
  { id: 'traffic', name: 'Traffic', format: 'number' }
]

const MODELS = [
  { id: 'holt_winters', name: 'Holt-Winters (Recommended)' },
  { id: 'linear', name: 'Linear Regression' },
  { id: 'exponential', name: 'Exponential Smoothing' },
  { id: 'moving_average', name: 'Moving Average' },
  { id: 'seasonal', name: 'Seasonal' }
]

const ForecastingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'forecast' | 'trends' | 'scenarios' | 'anomalies'>('forecast')
  const [loading, setLoading] = useState(false)

  // Selections
  const [entityType, setEntityType] = useState('market')
  const [entityId, setEntityId] = useState('uk')
  const [metric, setMetric] = useState('revenue')
  const [model, setModel] = useState('holt_winters')
  const [periods, setPeriods] = useState(6)

  // Data
  const [forecast, setForecast] = useState<Forecast | null>(null)
  const [historical, setHistorical] = useState<HistoricalPoint[]>([])
  const [trends, setTrends] = useState<Record<string, TrendData>>({})
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [anomalies, setAnomalies] = useState<Anomaly[]>([])
  const [healthScore, setHealthScore] = useState<number>(0)

  const entities = [
    { type: 'market', id: 'uk', name: 'United Kingdom' },
    { type: 'market', id: 'de', name: 'Germany' },
    { type: 'market', id: 'fr', name: 'France' },
    { type: 'market', id: 'es', name: 'Spain' },
    { type: 'market', id: 'it', name: 'Italy' }
  ]

  const formatValue = (value: number, format: string) => {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(value)
      case 'percent':
        return `${value.toFixed(1)}%`
      default:
        return new Intl.NumberFormat('en-GB').format(Math.round(value))
    }
  }

  const getMetricFormat = (metricId: string) => {
    return METRICS.find(m => m.id === metricId)?.format || 'number'
  }

  const fetchForecast = useCallback(async () => {
    setLoading(true)
    try {
      const [forecastRes, historicalRes] = await Promise.all([
        fetch(`${API_BASE}/forecast/${entityType}/${entityId}?metric=${metric}&model=${model}&periods=${periods}`),
        fetch(`${API_BASE}/historical/${entityType}/${entityId}?limit=24`)
      ])

      if (forecastRes.ok) {
        setForecast(await forecastRes.json())
      }
      if (historicalRes.ok) {
        setHistorical(await historicalRes.json())
      }
    } catch (err) {
      console.error('Forecast error:', err)
    } finally {
      setLoading(false)
    }
  }, [entityType, entityId, metric, model, periods])

  const fetchTrends = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/trends/${entityType}/${entityId}`)
      if (res.ok) {
        const data = await res.json()
        setTrends(data.trends || {})
        setHealthScore(data.overall_health_score || 0)
      }
    } catch (err) {
      console.error('Trends error:', err)
    } finally {
      setLoading(false)
    }
  }, [entityType, entityId])

  const fetchScenarios = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/scenarios/${entityType}/${entityId}?metric=${metric}`)
      if (res.ok) {
        const data = await res.json()
        setScenarios(data.scenarios || [])
      }
    } catch (err) {
      console.error('Scenarios error:', err)
    } finally {
      setLoading(false)
    }
  }, [entityType, entityId, metric])

  const fetchAnomalies = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/anomalies/${entityType}/${entityId}?metric=${metric}`)
      if (res.ok) {
        const data = await res.json()
        setAnomalies(data.anomalies || [])
      }
    } catch (err) {
      console.error('Anomalies error:', err)
    } finally {
      setLoading(false)
    }
  }, [entityType, entityId, metric])

  useEffect(() => {
    if (activeTab === 'forecast') {
      fetchForecast()
    } else if (activeTab === 'trends') {
      fetchTrends()
    } else if (activeTab === 'scenarios') {
      fetchScenarios()
    } else if (activeTab === 'anomalies') {
      fetchAnomalies()
    }
  }, [activeTab, fetchForecast, fetchTrends, fetchScenarios, fetchAnomalies])

  const renderSparkline = (data: number[], maxHeight: number = 40) => {
    if (!data.length) return null
    const max = Math.max(...data)
    const min = Math.min(...data)
    const range = max - min || 1
    const width = 120
    const step = width / (data.length - 1)

    const points = data.map((v, i) => {
      const x = i * step
      const y = maxHeight - ((v - min) / range) * maxHeight
      return `${x},${y}`
    }).join(' ')

    return (
      <svg width={width} height={maxHeight} style={{ marginLeft: '8px' }}>
        <polyline
          points={points}
          fill="none"
          stroke="#4A90E2"
          strokeWidth="2"
        />
      </svg>
    )
  }

  const styles: Record<string, React.CSSProperties> = {
    container: { minHeight: '100vh', backgroundColor: '#f5f7fa' },
    content: { padding: '32px', maxWidth: '1400px', margin: '0 auto' },
    title: { fontSize: '28px', fontWeight: 700, color: '#1F2937', marginBottom: '8px' },
    subtitle: { color: '#6B7280', marginBottom: '24px' },
    tabs: { display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #E5E7EB', paddingBottom: '16px' },
    tab: { padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 500, backgroundColor: '#F3F4F6', color: '#6B7280' },
    tabActive: { backgroundColor: '#4A90E2', color: 'white' },
    controls: { display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' as const, alignItems: 'flex-end' },
    controlGroup: { display: 'flex', flexDirection: 'column' as const, gap: '6px' },
    label: { fontSize: '13px', fontWeight: 500, color: '#6B7280' },
    select: { padding: '10px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', backgroundColor: 'white', minWidth: '150px' },
    button: { padding: '10px 20px', backgroundColor: '#4A90E2', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 },
    card: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    cardTitle: { fontSize: '18px', fontWeight: 600, color: '#1F2937', marginBottom: '16px' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: 'white', borderRadius: '12px', padding: '20px', textAlign: 'center' as const, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    statValue: { fontSize: '28px', fontWeight: 700, color: '#1F2937' },
    statLabel: { fontSize: '13px', color: '#6B7280', marginTop: '4px' },
    table: { width: '100%', borderCollapse: 'collapse' as const },
    th: { textAlign: 'left' as const, padding: '12px', borderBottom: '2px solid #E5E7EB', color: '#6B7280', fontSize: '13px', fontWeight: 600 },
    td: { padding: '12px', borderBottom: '1px solid #F3F4F6', color: '#374151', fontSize: '14px' },
    badge: { display: 'inline-block', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 500 },
    upBadge: { backgroundColor: '#D1FAE5', color: '#059669' },
    downBadge: { backgroundColor: '#FEE2E2', color: '#DC2626' },
    stableBadge: { backgroundColor: '#FEF3C7', color: '#D97706' },
    chartContainer: { height: '300px', marginTop: '20px', position: 'relative' as const },
    barChart: { display: 'flex', alignItems: 'flex-end', height: '200px', gap: '4px', marginTop: '20px' },
    bar: { flex: 1, backgroundColor: '#4A90E2', borderRadius: '4px 4px 0 0', transition: 'height 0.3s' },
    forecastBar: { backgroundColor: '#A855F7' },
    scenarioCard: { padding: '16px', border: '1px solid #E5E7EB', borderRadius: '8px', marginBottom: '12px' },
    anomalyCard: { padding: '16px', borderRadius: '8px', marginBottom: '12px', borderLeft: '4px solid' },
    healthMeter: { width: '100%', height: '8px', backgroundColor: '#E5E7EB', borderRadius: '4px', overflow: 'hidden', marginTop: '8px' },
    healthFill: { height: '100%', borderRadius: '4px', transition: 'width 0.5s' }
  }

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <h1 style={styles.title}>Predictive Analytics & Forecasting</h1>
        <p style={styles.subtitle}>Generate forecasts, analyze trends, and explore scenarios</p>

        <div style={styles.tabs}>
          {(['forecast', 'trends', 'scenarios', 'anomalies'] as const).map(tab => (
            <button
              key={tab}
              style={{ ...styles.tab, ...(activeTab === tab ? styles.tabActive : {}) }}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div style={styles.controls}>
          <div style={styles.controlGroup}>
            <label style={styles.label}>Entity</label>
            <select
              value={`${entityType}:${entityId}`}
              onChange={e => {
                const [type, id] = e.target.value.split(':')
                setEntityType(type)
                setEntityId(id)
              }}
              style={styles.select}
            >
              {entities.map(e => (
                <option key={`${e.type}:${e.id}`} value={`${e.type}:${e.id}`}>
                  {e.name}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.controlGroup}>
            <label style={styles.label}>Metric</label>
            <select value={metric} onChange={e => setMetric(e.target.value)} style={styles.select}>
              {METRICS.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>

          {activeTab === 'forecast' && (
            <>
              <div style={styles.controlGroup}>
                <label style={styles.label}>Model</label>
                <select value={model} onChange={e => setModel(e.target.value)} style={styles.select}>
                  {MODELS.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>Periods</label>
                <select value={periods} onChange={e => setPeriods(Number(e.target.value))} style={styles.select}>
                  <option value={3}>3 months</option>
                  <option value={6}>6 months</option>
                  <option value={12}>12 months</option>
                </select>
              </div>
            </>
          )}
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
            Loading...
          </div>
        )}

        {activeTab === 'forecast' && forecast && !loading && (
          <>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statValue}>
                  {formatValue(forecast.summary.total_forecast, getMetricFormat(metric))}
                </div>
                <div style={styles.statLabel}>Total Forecast ({periods}m)</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>
                  {formatValue(forecast.summary.average_forecast, getMetricFormat(metric))}
                </div>
                <div style={styles.statLabel}>Monthly Average</div>
              </div>
              <div style={styles.statCard}>
                <div style={{
                  ...styles.statValue,
                  color: forecast.summary.projected_growth_pct >= 0 ? '#059669' : '#DC2626'
                }}>
                  {forecast.summary.projected_growth_pct >= 0 ? '+' : ''}{forecast.summary.projected_growth_pct}%
                </div>
                <div style={styles.statLabel}>Projected Growth</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>
                  <span style={{
                    ...styles.badge,
                    ...(forecast.summary.trend_direction === 'increasing' ? styles.upBadge :
                        forecast.summary.trend_direction === 'decreasing' ? styles.downBadge : styles.stableBadge)
                  }}>
                    {forecast.summary.trend_direction}
                  </span>
                </div>
                <div style={styles.statLabel}>Trend Direction</div>
              </div>
            </div>

            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Forecast vs Historical</h3>
              <div style={styles.barChart}>
                {historical.slice(-12).map((h, i) => (
                  <div
                    key={i}
                    style={{
                      ...styles.bar,
                      height: `${(h[metric as keyof HistoricalPoint] as number / Math.max(...historical.map(x => x[metric as keyof HistoricalPoint] as number))) * 180}px`
                    }}
                    title={`${h.month}: ${formatValue(h[metric as keyof HistoricalPoint] as number, getMetricFormat(metric))}`}
                  />
                ))}
                {forecast.forecasts.map((f, i) => (
                  <div
                    key={`f-${i}`}
                    style={{
                      ...styles.bar,
                      ...styles.forecastBar,
                      height: `${(f.forecast / Math.max(...historical.map(x => x[metric as keyof HistoricalPoint] as number))) * 180}px`,
                      opacity: 0.8
                    }}
                    title={`${f.period}: ${formatValue(f.forecast, getMetricFormat(metric))} (forecast)`}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', gap: '24px', marginTop: '12px', justifyContent: 'center' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#6B7280' }}>
                  <span style={{ width: '12px', height: '12px', backgroundColor: '#4A90E2', borderRadius: '2px' }} />
                  Historical
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#6B7280' }}>
                  <span style={{ width: '12px', height: '12px', backgroundColor: '#A855F7', borderRadius: '2px' }} />
                  Forecast
                </span>
              </div>
            </div>

            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Forecast Details</h3>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Period</th>
                    <th style={styles.th}>Forecast</th>
                    <th style={styles.th}>Lower Bound (95%)</th>
                    <th style={styles.th}>Upper Bound (95%)</th>
                  </tr>
                </thead>
                <tbody>
                  {forecast.forecasts.map((f, i) => (
                    <tr key={i}>
                      <td style={styles.td}>{f.period}</td>
                      <td style={{ ...styles.td, fontWeight: 600 }}>
                        {formatValue(f.forecast, getMetricFormat(metric))}
                      </td>
                      <td style={styles.td}>{formatValue(f.lower_bound, getMetricFormat(metric))}</td>
                      <td style={styles.td}>{formatValue(f.upper_bound, getMetricFormat(metric))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'trends' && !loading && (
          <>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Overall Health Score</h3>
              <div style={{ fontSize: '48px', fontWeight: 700, color: healthScore >= 70 ? '#059669' : healthScore >= 40 ? '#D97706' : '#DC2626' }}>
                {healthScore.toFixed(0)}%
              </div>
              <div style={styles.healthMeter}>
                <div style={{
                  ...styles.healthFill,
                  width: `${healthScore}%`,
                  backgroundColor: healthScore >= 70 ? '#059669' : healthScore >= 40 ? '#D97706' : '#DC2626'
                }} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              {Object.entries(trends).map(([metricKey, data]) => {
                const metricInfo = METRICS.find(m => m.id === metricKey)
                return (
                  <div key={metricKey} style={styles.card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
                          {metricInfo?.name || metricKey}
                        </h4>
                        <div style={{ fontSize: '24px', fontWeight: 700, color: '#1F2937' }}>
                          {formatValue(data.current_value, metricInfo?.format || 'number')}
                        </div>
                      </div>
                      <span style={{
                        ...styles.badge,
                        ...(data.trend_direction === 'up' ? styles.upBadge :
                            data.trend_direction === 'down' ? styles.downBadge : styles.stableBadge)
                      }}>
                        {data.trend_direction === 'up' ? '↑' : data.trend_direction === 'down' ? '↓' : '→'} {data.trend_direction}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginTop: '16px' }}>
                      <div>
                        <div style={{ fontSize: '13px', color: '#6B7280' }}>YoY Growth</div>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: data.yoy_growth_pct >= 0 ? '#059669' : '#DC2626' }}>
                          {data.yoy_growth_pct >= 0 ? '+' : ''}{data.yoy_growth_pct}%
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '13px', color: '#6B7280' }}>MoM Growth</div>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: data.mom_growth_pct >= 0 ? '#059669' : '#DC2626' }}>
                          {data.mom_growth_pct >= 0 ? '+' : ''}{data.mom_growth_pct}%
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '13px', color: '#6B7280' }}>Volatility</div>
                        <div style={{ fontSize: '16px', fontWeight: 600 }}>{data.volatility}%</div>
                      </div>
                    </div>
                    {renderSparkline(historical.map(h => h[metricKey as keyof HistoricalPoint] as number))}
                  </div>
                )
              })}
            </div>
          </>
        )}

        {activeTab === 'scenarios' && !loading && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Scenario Analysis</h3>
            <p style={{ color: '#6B7280', marginBottom: '20px' }}>
              Compare different growth scenarios for {METRICS.find(m => m.id === metric)?.name || metric}
            </p>
            {scenarios.map((scenario, i) => (
              <div
                key={i}
                style={{
                  ...styles.scenarioCard,
                  borderColor: scenario.name === 'Baseline' ? '#4A90E2' : '#E5E7EB',
                  backgroundColor: scenario.name === 'Baseline' ? '#F0F7FF' : 'white'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>{scenario.name}</h4>
                    <p style={{ fontSize: '13px', color: '#6B7280' }}>{scenario.description}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#1F2937' }}>
                      {formatValue(scenario.total_forecast, getMetricFormat(metric))}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: scenario.difference_from_baseline >= 0 ? '#059669' : '#DC2626'
                    }}>
                      {scenario.difference_from_baseline >= 0 ? '+' : ''}{scenario.difference_from_baseline}% vs baseline
                    </div>
                  </div>
                </div>
                <div style={{
                  marginTop: '12px',
                  height: '8px',
                  backgroundColor: '#E5E7EB',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${Math.min((scenario.adjustment_factor / 1.3) * 100, 100)}%`,
                    height: '100%',
                    backgroundColor: scenario.name === 'Optimistic' || scenario.name === 'Aggressive' ? '#059669' :
                                   scenario.name === 'Pessimistic' ? '#DC2626' : '#4A90E2',
                    borderRadius: '4px'
                  }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'anomalies' && !loading && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Detected Anomalies</h3>
            <p style={{ color: '#6B7280', marginBottom: '20px' }}>
              Unusual data points detected in {METRICS.find(m => m.id === metric)?.name || metric}
            </p>
            {anomalies.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                No anomalies detected in the current data
              </div>
            ) : (
              anomalies.map((anomaly, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.anomalyCard,
                    borderLeftColor: anomaly.severity === 'high' ? '#DC2626' : '#F59E0B',
                    backgroundColor: anomaly.severity === 'high' ? '#FEF2F2' : '#FFFBEB'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: anomaly.type === 'spike' ? '#D1FAE5' : '#FEE2E2',
                        color: anomaly.type === 'spike' ? '#059669' : '#DC2626'
                      }}>
                        {anomaly.type === 'spike' ? '↑ Spike' : '↓ Dip'}
                      </span>
                      <span style={{ marginLeft: '12px', fontWeight: 600 }}>{anomaly.period}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 600 }}>
                        {formatValue(anomaly.value, getMetricFormat(metric))}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6B7280' }}>
                        {anomaly.deviation_percent}% deviation
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ForecastingPage
